import {makeEnumRuntimeType} from '../../models/api/types/enumTypes';
import {ResourceTableStatusesEnum} from '../../store/commonAppTypes';
import {UnreachableError} from './customErrors';

/*
 * Types.
 */

export enum AsyncStatusesEnum {
  LOADING = 'LOADING',
  LOADED = 'LOADED',
  FAILED = 'FAILED',
}

export enum AsyncMoreStatusesEnum {
  NONE = 'NONE',
  HAS_MORE = 'HAS_MORE',
  LOADING = 'LOADING',
  LOADING_MORE = 'LOADING_MORE',
  ALL_LOADED = 'ALL_LOADED',
  ERROR = 'ERROR',
}

export enum AsyncMutationStatusesEnum {
  IDLE = 'IDLE',
  LOADING = 'LOADING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

/*
 * Runtime types.
 */

export const RAsyncMoreStatus = makeEnumRuntimeType(AsyncMoreStatusesEnum);
export const RAsyncStatus = makeEnumRuntimeType(AsyncStatusesEnum);
export const RAsyncMutationStatus = makeEnumRuntimeType(AsyncMutationStatusesEnum);

/*
 * Helpers.
 */

export function combineAsyncStatuses(...statuses: ReadonlyArray<AsyncStatusesEnum>): AsyncStatusesEnum {
  if (statuses.includes(AsyncStatusesEnum.FAILED)) {
    return AsyncStatusesEnum.FAILED;
  }

  if (statuses.includes(AsyncStatusesEnum.LOADING)) {
    return AsyncStatusesEnum.LOADING;
  }

  return AsyncStatusesEnum.LOADED;
}

export function combineAsyncMoreStatuses(...statuses: ReadonlyArray<AsyncMoreStatusesEnum>) {
  if (statuses.some((status) => status === AsyncMoreStatusesEnum.ERROR)) {
    return AsyncMoreStatusesEnum.ERROR;
  }

  if (statuses.every((status) => status === AsyncMoreStatusesEnum.NONE)) {
    return AsyncMoreStatusesEnum.NONE;
  }

  if (statuses.some((status) => status === AsyncMoreStatusesEnum.LOADING)) {
    return AsyncMoreStatusesEnum.LOADING;
  }

  if (statuses.some((status) => status === AsyncMoreStatusesEnum.LOADING_MORE)) {
    return AsyncMoreStatusesEnum.LOADING_MORE;
  }

  if (statuses.some((status) => status === AsyncMoreStatusesEnum.HAS_MORE)) {
    return AsyncMoreStatusesEnum.HAS_MORE;
  }

  return AsyncMoreStatusesEnum.ALL_LOADED;
}

export function asyncMoreStatusToAsyncStatus(asyncMoreStatus: AsyncMoreStatusesEnum): AsyncStatusesEnum {
  switch (asyncMoreStatus) {
    case AsyncMoreStatusesEnum.ALL_LOADED:
    case AsyncMoreStatusesEnum.HAS_MORE:
    case AsyncMoreStatusesEnum.NONE:
      return AsyncStatusesEnum.LOADED;

    case AsyncMoreStatusesEnum.LOADING:
    case AsyncMoreStatusesEnum.LOADING_MORE:
      return AsyncStatusesEnum.LOADING;

    case AsyncMoreStatusesEnum.ERROR:
      return AsyncStatusesEnum.FAILED;

    default:
      throw new UnreachableError(asyncMoreStatus);
  }
}

export function asyncStatusToAsyncMoreStatus(asyncStatus: AsyncStatusesEnum): AsyncMoreStatusesEnum {
  switch (asyncStatus) {
    case AsyncStatusesEnum.LOADING:
      return AsyncMoreStatusesEnum.LOADING;

    case AsyncStatusesEnum.LOADED:
      return AsyncMoreStatusesEnum.ALL_LOADED;

    case AsyncStatusesEnum.FAILED:
      return AsyncMoreStatusesEnum.ERROR;

    default:
      throw new UnreachableError(asyncStatus);
  }
}

export function computeResourceTableStatusToAsyncStatus(
  status: ResourceTableStatusesEnum,
  {
    shouldTreatInitialAsLoaded = false,
  }: {
    /**
     * @deprecated Sets the initial state to loaded rather than loading. Added to de-risk consolidating
     * callers onto computeResourceTableStatusToAsyncStatus. Callers which use shouldTreatInitialAsLoaded
     * should be fixed, so that they no longer require shouldTreatInitialAsLoaded.
     */
    shouldTreatInitialAsLoaded?: boolean;
  } = {},
): AsyncStatusesEnum {
  switch (status) {
    case ResourceTableStatusesEnum.INITIAL:
      return shouldTreatInitialAsLoaded ? AsyncStatusesEnum.LOADED : AsyncStatusesEnum.LOADING;

    case ResourceTableStatusesEnum.LOADING:
      return AsyncStatusesEnum.LOADING;

    case ResourceTableStatusesEnum.LOADED:
      return AsyncStatusesEnum.LOADED;

    case ResourceTableStatusesEnum.FAILED:
      return AsyncStatusesEnum.FAILED;

    default:
      throw new UnreachableError(status);
  }
}

export function combineAsyncMutationStatuses(
  ...statuses: ReadonlyArray<AsyncMutationStatusesEnum>
): AsyncMutationStatusesEnum {
  if (statuses.includes(AsyncMutationStatusesEnum.ERROR)) {
    return AsyncMutationStatusesEnum.ERROR;
  }

  if (statuses.includes(AsyncMutationStatusesEnum.LOADING)) {
    return AsyncMutationStatusesEnum.LOADING;
  }

  if (statuses.includes(AsyncMutationStatusesEnum.IDLE)) {
    return AsyncMutationStatusesEnum.IDLE;
  }

  return AsyncMutationStatusesEnum.SUCCESS;
}
